import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building, Save, Mail, Phone, Globe, MapPin, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolSettings() {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    name: '',
    logo_url: '',
    address: '',
    city: '',
    email: '',
    phone: '',
    website: '',
    motto: '',
    kra_pin: '',
    registration_no: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/school`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSettings({
        name: data.name || '',
        logo_url: data.logo_url || '',
        address: data.address || '',
        city: data.city || '',
        email: data.email || '',
        phone: data.phone || '',
        website: data.website || '',
        motto: data.motto || '',
        kra_pin: data.kra_pin || '',
        registration_no: data.registration_no || ''
      });
    } catch (error) {
      toast.error('Failed to fetch school settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const res = await fetch(`${API_URL}/api/settings/school`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('School settings updated successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update settings');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="school-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">School Settings</h1>
          <p className="text-slate-500">Configure school details for reports and documents</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Basic Information
              </CardTitle>
              <CardDescription>School name and branding details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>School Name *</Label>
                <Input
                  value={settings.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., Academion Academy"
                  required
                />
              </div>
              <div>
                <Label>School Motto</Label>
                <Input
                  value={settings.motto}
                  onChange={(e) => handleChange('motto', e.target.value)}
                  placeholder="e.g., Excellence in Education"
                />
              </div>
              <div>
                <Label>Logo URL</Label>
                <Input
                  value={settings.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
                {settings.logo_url && (
                  <div className="mt-2 p-4 bg-slate-50 rounded-lg flex justify-center">
                    <img 
                      src={settings.logo_url} 
                      alt="School Logo" 
                      className="h-16 object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Contact Information
              </CardTitle>
              <CardDescription>Contact details displayed on documents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={settings.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="info@school.ac.ke"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={settings.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+254 700 000 000"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={settings.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="www.school.ac.ke"
                    className="pl-10"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Address
              </CardTitle>
              <CardDescription>Physical and postal address</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>P.O. Box / Street Address</Label>
                <Input
                  value={settings.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="P.O. Box 12345 - 00100"
                />
              </div>
              <div>
                <Label>City / Location</Label>
                <Input
                  value={settings.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Nairobi, Kenya"
                />
              </div>
            </CardContent>
          </Card>

          {/* Registration Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Registration Details
              </CardTitle>
              <CardDescription>Official registration numbers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>KRA PIN</Label>
                <Input
                  value={settings.kra_pin}
                  onChange={(e) => handleChange('kra_pin', e.target.value)}
                  placeholder="P000000000A"
                />
              </div>
              <div>
                <Label>Registration Number</Label>
                <Input
                  value={settings.registration_no}
                  onChange={(e) => handleChange('registration_no', e.target.value)}
                  placeholder="School registration number"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Document Header Preview</CardTitle>
            <CardDescription>This is how your school details will appear on reports and documents</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-6 border rounded-lg bg-white text-center space-y-1">
              {settings.logo_url && (
                <img 
                  src={settings.logo_url} 
                  alt="School Logo" 
                  className="h-12 mx-auto mb-2 object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <h2 className="text-xl font-bold text-slate-900">{settings.name || 'School Name'}</h2>
              {settings.motto && (
                <p className="text-sm italic text-slate-600">"{settings.motto}"</p>
              )}
              <p className="text-sm text-slate-600">
                {[settings.address, settings.city].filter(Boolean).join(', ') || 'Address'}
              </p>
              <p className="text-sm text-slate-600">
                {[
                  settings.phone && `Tel: ${settings.phone}`,
                  settings.email && `Email: ${settings.email}`
                ].filter(Boolean).join(' | ') || 'Contact Information'}
              </p>
              {settings.website && (
                <p className="text-sm text-slate-500">{settings.website}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end mt-6">
          <Button type="submit" disabled={saving} className="min-w-32" data-testid="save-settings-btn">
            {saving ? (
              'Saving...'
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" /> Save Settings
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
